import type { Alignment, Placement } from '@floating-ui/react';
import {
  arrow,
  autoPlacement,
  FloatingArrow,
  FloatingPortal,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
} from '@floating-ui/react';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import styles from './Tooltip.module.scss';

// Maintains backwards compatibility after migrating from popper to floating-ui
const autoPlacementValues = ['auto', 'auto-start', 'auto-end'] as const;
type AutoPlacement = (typeof autoPlacementValues)[number];

export interface TooltipProps {
  children: React.ReactNode;
  tooltip: React.ReactNode | string | (() => React.ReactElement);
  position?: Placement | AutoPlacement;
  delay?: number;
  className?: string;
  childrenClassName?: string;
  fixed?: boolean;
  enabled?: boolean;
  isOpen?: boolean;
  /** Opens when the trigger element gets focus */
  openOnFocus?: boolean;
  /** Enable portal rendering */
  usePortal?: boolean;
  /** Container for the portal, by default it'll be mounted outside of app root into the body */
  portalRoot?: HTMLElement | null | React.MutableRefObject<HTMLElement | null>;
  element?: 'button' | 'div' | 'span';
  /** Styles for the children container */
  style?: React.CSSProperties;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
}

const Tooltip: React.FC<TooltipProps> = ({
  tooltip,
  children,
  position = 'auto',
  delay = 0,
  fixed,
  className,
  childrenClassName,
  style,
  enabled = true,
  isOpen: isOpenFromProps,
  openOnFocus = false,
  usePortal = false,
  portalRoot,
  element,
  onClick,
}) => {
  const [isOpenInState, setIsOpen] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  useEffect(() => {
    setIsTouchDevice(window.matchMedia('(pointer: coarse)').matches);
  }, []);

  const isOpen = (isOpenFromProps ?? isOpenInState) && !!tooltip;

  const arrowRef = useRef(null);

  const isAutoPlacement = autoPlacementValues.includes(position as AutoPlacement);
  const alignment = getAutoAlignmentFromPlacement(position);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    strategy: fixed ? 'fixed' : undefined,
    placement: isAutoPlacement ? 'bottom' : (position as Placement), // bottom is the default
    middleware: [
      offset(10),
      ...(isAutoPlacement
        ? [
            autoPlacement({
              alignment,
            }),
          ]
        : [flip()]),
      shift(),
      arrow({
        element: arrowRef,
      }),
    ],
  });

  const hover = useHover(context, {
    move: false,
    enabled: isOpenFromProps === undefined && !isTouchDevice,
    delay: delay ? { open: delay } : undefined,
  });

  const focus = useFocus(context, {
    enabled: openOnFocus && !isOpenFromProps,
  });

  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss]);
  const Tooltip = tooltip;
  const Element = element || 'div';

  const tooltipContent = (
    <div
      ref={refs.setFloating}
      className={classNames(styles.container, className)}
      {...getFloatingProps()}
      style={floatingStyles}
    >
      <FloatingArrow ref={arrowRef} context={context} />
      {typeof Tooltip === 'function' ? <Tooltip /> : Tooltip}
    </div>
  );

  return (
    <>
      <Element
        ref={refs.setReference}
        className={childrenClassName}
        style={style}
        {...getReferenceProps()}
        onClick={onClick}
      >
        {children}
      </Element>
      {isOpen && enabled ? (
        usePortal ? (
          <FloatingPortal root={portalRoot}>{tooltipContent}</FloatingPortal>
        ) : (
          tooltipContent
        )
      ) : null}
    </>
  );
};

function getAutoAlignmentFromPlacement(
  placement: Placement | AutoPlacement
): Alignment | undefined {
  if (!autoPlacementValues.includes(placement as AutoPlacement)) return undefined;
  return placement.endsWith('start') ? 'start' : placement.endsWith('end') ? 'end' : undefined;
}

export default Tooltip;
